// Lazy load components in routes.js
import React, { lazy } from "react";
import { Routes, Route } from "react-router-dom";
import RootLayout from "./layouts/RootLayout";
import AdminLayout from "./layouts/AdminLayout";
import PrivateRoute from "./components/PrivateRoute";
import useTokenExpiration from "./hooks/useTokenExpiration";
import ViewPaLink from "./pages/ViewPaLink";
import DownloadBox from "./pages/DownloadBox";
import EditAccount from "./pages/Admin/Operations/UserAccounts/EditAccount";
import ACGTCardEdit from "./pages/Admin/Operations/ACGTCards/ACGTCardEdit.js";
import ACGTCardTransactionHistory from "./pages/Admin/Operations/ACGTCards/ACGTCardTransactionHistory.js";
import ACGTCardPaymentHistory from "./pages/Admin/Operations/ACGTCards/ACGTCardPaymentHistory.js";
import DispatchEmail from "./pages/Admin/Operations/UserOrders/DispatchEmail.js";
import PriceTable from "./pages/Admin/PriceTables/PriceTable.js";
import PriceChange from "./pages/Admin/PriceTables/PriceChange.js";
import PALinks from "./pages/Admin/Operations/UserOrders/PALinks.js";
import ACGTCardPaymentHistoryEdit from "./pages/Admin/Operations/ACGTCards/ACGTCardPaymentHistoryEdit.js";
import ACGTCardTransactionHistoryEdit from "./pages/Admin/Operations/ACGTCards/ACGTCardTransactionHistoryEdit.js";
import UploadPrimerExcel from "./pages/Admin/Operations/UserAccounts/UploadPrimerExcel";
import InvoiceUpload from "./pages/Admin/Operations/UserOrders/InvoiceUpload";
import InvoiceView from "./pages/Admin/Operations/UserOrders/InvoiceView";
import FileUploadDlg from "./pages/Admin/Operations/UserOrders/FileUploadDlg";
import CorporateDetails from "./pages/Admin/Operations/Corporate/CorporateDetails";
import PrivateRoute2 from "./components/PrivateRoute2";
import AuthLogin from "./pages/Admin/AuthLogin";
import PrivateRoute3 from "./components/PrivateRoute3.js";
import ViewOrderAdmin from "./pages/Admin/Operations/UserOrders/ViewOrderAdmin.jsx";
import QRCodeForm from "./pages/OrderForms/QRSignUp/index.js";
import Unsubscribe from "./pages/Unsubscribe.js";
import PopupDetail from "./pages/Admin/Operations/Popups/PopupDetail";
import WpsForm from "./pages/OrderForms/WpsForm/index.js";
import Survey from "./pages/Survey.js";
import SurveyWPS from "./pages/SurveyWPS.js";
import DropboxDetails from "./pages/Admin/Operations/Dropboxes/DropboxDetails.js";
import DropboxCorporates from "./pages/Admin/Operations/Dropboxes/DropboxCorporates.js";
import DiscountDetail from "./pages/Admin/Operations/Discounts/DiscountDetail.js";
import DiscountCoupons from "./pages/Admin/DiscountCoupons.js";
import ReferFriend from "./pages/ReferFriend.js";
import Referrals from "./pages/Admin/Referrals.js";
import WpsDqcForm from "./pages/OrderForms/WpsDqcForm/index.js";

const Login = lazy(() => import("./pages/Auth/Login"));
const Register = lazy(() => import("./pages/Auth/Register"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/Auth/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FsaForm = lazy(() => import("./pages/OrderForms/FsaForm"));
const HtpForm = lazy(() => import("./pages/OrderForms/HtpForm"));
const FragForm = lazy(() => import("./pages/OrderForms/FragmentAnalysisForm"));
const DcssForm = lazy(() => import("./pages/OrderForms/DcssForm"));
const SppeForm = lazy(() => import("./pages/OrderForms/SppeForm"));
const NgsForm = lazy(() => import("./pages/OrderForms/NgsForm"));
const PrimerForm = lazy(() => import("./pages/OrderForms/PrimerForm"));
const ContractForm = lazy(() => import("./pages/OrderForms/ContractForm"));
const GenomicForm = lazy(() => import("./pages/OrderForms/GenomicForm"));
const QuoteRequestForm = lazy(() =>
  import("./pages/OrderForms/QuoteRequestForm")
);
const FreeTrialForm = lazy(() => import("./pages/OrderForms/FreeTrialForm"));
const FreeTrialFormWPS = lazy(() => import("./pages/OrderForms/FreeTrialFormWPS"));
const Profile = lazy(() => import("./pages/Profile"));
const ACGTCard = lazy(() => import("./pages/ACGTCard"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const PrimerBank = lazy(() => import("./pages/PrimerBank"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ViewOrder = lazy(() => import("./pages/ViewOrder"));
const Invoice = lazy(() => import("./pages/Invoice"));

// Admin Routes
const UserOrders = lazy(() => import("./pages/Admin/UserOrders"));
const UserAccounts = lazy(() => import("./pages/Admin/UserAccounts"));
const CorporateAccounts = lazy(() => import("./pages/Admin/CorporateAccounts"));
const Dropboxes = lazy(() => import("./pages/Admin/Dropboxes"));
// const CorporateUsers = lazy(() => import("./pages/Admin/Operations/Corporate/CorporateUsers"));
// const CorporatePrices = lazy(() => import("./pages/Admin/Operations/Corporate/CorporatePrices"));
const QuoteRequests = lazy(() => import("./pages/Admin/QuoteRequests"));
const AdminAcgtCards = lazy(() => import("./pages/Admin/AcgtCards"));
const PopupMessages = lazy(() => import("./pages/Admin/PopupMessages"));
const Statistics = lazy(() => import("./pages/Admin/Statistics"));
const Settings = lazy(() => import("./pages/Admin/Settings"));

export const AppRoutes = () => {
  useTokenExpiration(); //Check token expiration on each render
  return (
    <Routes>
		<Route path="acgtQR" element={<QRCodeForm />} />
		<Route path="acgtQRraffle" element={<QRCodeForm />} />
		<Route path="unsubscribe" element={<Unsubscribe />} />
		<Route path="/" element={<RootLayout />}>
        <Route index element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="forgotpassword" element={<ForgotPassword />} />
        <Route path="resetpassword" element={<ResetPassword />} />
        <Route path="quoterequest" element={<QuoteRequestForm />} />
			<Route element={<PrivateRoute />}>
				<Route path="dashboard" element={<Dashboard />} />
				<Route path="orders" element={<PrivateRoute3 />}>
					<Route path="fsa" element={<FsaForm />} />
					<Route path="htp" element={<HtpForm />} />
					<Route path="fla" element={<FragForm />} />
					<Route path="dcss" element={<DcssForm />} />
					<Route path="sppe" element={<SppeForm />} />
					<Route path="ngs" element={<NgsForm />} />
					<Route path="wps" element={<WpsForm />} />
					<Route path="wpsDqc" element={<WpsDqcForm/>} />
					<Route path="primer" element={<PrimerForm />} />
					<Route path="contract" element={<ContractForm />} />
					<Route path="genomic" element={<GenomicForm />} />
					<Route path="freetrial" element={<FreeTrialForm />} />
					{/* <Route path="freetrialwps" element={<FreeTrialFormWPS />} /> */}
				</Route>
				<Route path="refer-a-friend" element={<ReferFriend />} />
				<Route path="survey" element={<Survey />} />
				<Route path="surveywps" element={<SurveyWPS />} />
				<Route path="profile" element={<Profile />} />
				<Route path="acgtcard" element={<ACGTCard />} />
				<Route path="orderhistory" element={<MyOrders />} />
				<Route path="primerbank" element={<PrimerBank />} />
				<Route path="pricing" element={<Pricing />} />
			</Route>
      </Route>

      <Route path="/vieworderdetails" element={<ViewOrder />} />
      <Route path="/vieworderinvoice" element={<Invoice />} />
      <Route path="/viewdownloadbox" element={<DownloadBox />} />
      <Route path="/viewpalink" element={<ViewPaLink />} />
      <Route path="adminlogin" element={<AuthLogin/>} />
      <Route element={<PrivateRoute2 />}>
			<Route path='/editcorporate' element={<CorporateDetails />} />
			<Route path='/editpopupdetails' element={<PopupDetail />} />
			<Route path='/editdiscountdetails' element={<DiscountDetail />} />
			<Route path="/editaccount" element={<EditAccount />} />
			<Route path='/uploadprimerexcel' element={<UploadPrimerExcel />} />
			<Route path='/uploadinvoice' element={<InvoiceUpload />} />
			<Route path='/viewinvoice' element={<InvoiceView />} />
			<Route path='/uploadresearch' element={<FileUploadDlg />} />
			<Route path='/editdropbox' element={<DropboxDetails />} />
			<Route path='/showDropboxCorporates' element={<DropboxCorporates />} />
          <Route path="/vieworderdetailsadmin" element={<ViewOrderAdmin />} />
          <Route path="/acgtcarddetails" element={<ACGTCardEdit />} />
          <Route path="/palinks" element={<PALinks />} />
          <Route path="/acgtcardpayments" element={<ACGTCardPaymentHistory />} />
          <Route
            path="/editacgtcardpayments"
            element={<ACGTCardPaymentHistoryEdit />}
          />
          <Route
            path="/acgtcardtransactions"
            element={<ACGTCardTransactionHistory />}
          />
          <Route
            path="/editacgtcardtransaction"
            element={<ACGTCardTransactionHistoryEdit />}
          />
          <Route path="/dispatchemail" element={<DispatchEmail />} />
          <Route path="/viewpricing" element={<PriceTable />} />
          <Route path="/pricecopy" element={<PriceChange />} />
          <Route path="admin" element={<AdminLayout />}>
                <Route path="user">
                  <Route path="orders" element={<UserOrders />} />
                  <Route path="accounts" element={<UserAccounts />} />
                </Route>
                <Route path="corporate">
                  <Route path="accounts" element={<CorporateAccounts />} />
                  {/* <Route path='users' element={<CorporateUsers />} /> */}
                  {/* <Route path='prices' element={<CorporatePrices />} /> */}
                </Route>
                <Route path="quoterequests" element={<QuoteRequests />} />
                <Route path="acgtcards" element={<AdminAcgtCards />} />
				<Route path="popups" element={<PopupMessages />} />
				<Route path="discounts" element={<DiscountCoupons />} />
				<Route path="referrals" element={<Referrals />} />
				<Route path="stats" element={<Statistics />} />
				<Route path="dropboxes" element={<Dropboxes />} />
                <Route path="settings" element={<Settings />} />
          </Route>
      </Route>
      
    </Routes>
  );
};
